import React from 'react';
import { 
  Target, 
  Compass, 
  Network, 
  Zap, 
  Rocket, 
  Calendar,
  User,
  TrendingUp,
  Users,
  GraduationCap,
  Music,
  Building2,
  Sparkles,
  Clock,
  CheckCircle2
} from 'lucide-react';

const EmergentPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="pt-16 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_tech-baenae-hub/artifacts/fc1n8k2w_d9f9328957cc866bf56a8f5279cb4d176711a1fb.png"
            alt="Emergent"
            className="h-12 md:h-14 mx-auto mb-8"
          />
          <h2 className="text-2xl md:text-3xl font-light bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4">
            Brand, Infrastructure & Ecosystem Strategy
          </h2>
          <p className="text-white/50 text-lg">Naomi Thomas</p>
        </div>
      </header>

      {/* Content */}
      <main className="px-6 pb-24">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Positioning */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Positioning</h3>
            </div>
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-3xl p-8 border border-white/10">
              <p className="text-white/80 text-lg leading-relaxed mb-4">
                Emergent has strong product velocity and serious builder traction.
              </p>
              <p className="text-white/60 text-lg leading-relaxed">
                The next layer of growth is ecosystem infrastructure and intentional brand positioning that signals clarity, intelligence, and momentum to founders at every stage.
              </p>
            </div>
          </section>

          {/* Brand Direction */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <Compass className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Brand Direction</h3>
            </div>
            <p className="text-white/60 text-lg mb-6">Emergent should be perceived as:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors">
                <TrendingUp className="w-8 h-8 text-cyan-400 mb-4" />
                <p className="text-white/80">The platform early-stage founders grow with</p>
              </div>
              <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors">
                <Rocket className="w-8 h-8 text-blue-400 mb-4" />
                <p className="text-white/80">A serious tool for builders shipping real products</p>
              </div>
              <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors">
                <Users className="w-8 h-8 text-cyan-400 mb-4" />
                <p className="text-white/80">Accessible to non-technical founders without intimidation</p>
              </div>
              <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors">
                <Sparkles className="w-8 h-8 text-blue-400 mb-4" />
                <p className="text-white/80">Strategic, not chaotic</p>
              </div>
            </div>
            <p className="text-cyan-400/60 text-sm mt-8 text-center italic">Brand perception shapes adoption velocity.</p>
          </section>

          {/* Ecosystem Strategy */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <Network className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Ecosystem Strategy</h3>
            </div>
            <p className="text-white/60 text-lg mb-8">Adoption compounds inside strong ecosystems:</p>
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="flex items-center gap-3 bg-white/5 rounded-full px-5 py-3 border border-white/10">
                <Building2 className="w-5 h-5 text-cyan-400" />
                <span className="text-white/80">Accelerator environments</span>
              </div>
              <div className="flex items-center gap-3 bg-white/5 rounded-full px-5 py-3 border border-white/10">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-white/80">Founder houses</span>
              </div>
              <div className="flex items-center gap-3 bg-white/5 rounded-full px-5 py-3 border border-white/10">
                <GraduationCap className="w-5 h-5 text-cyan-400" />
                <span className="text-white/80">University innovation labs</span>
              </div>
              <div className="flex items-center gap-3 bg-white/5 rounded-full px-5 py-3 border border-white/10">
                <Music className="w-5 h-5 text-blue-400" />
                <span className="text-white/80">Creator and music tech networks</span>
              </div>
              <div className="flex items-center gap-3 bg-white/5 rounded-full px-5 py-3 border border-white/10">
                <Zap className="w-5 h-5 text-cyan-400" />
                <span className="text-white/80">Tech weeks and curated gatherings</span>
              </div>
            </div>
            <p className="text-cyan-400/60 text-sm text-center italic">When respected builders adopt, communities follow.</p>
          </section>

          {/* Builder Activations */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Builder Activations</h3>
            </div>
            <p className="text-white/60 text-lg mb-8">Curated builder activations in high-signal environments:</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/5 p-6 border border-cyan-500/20">
                <GraduationCap className="w-10 h-10 text-cyan-400 mb-4" />
                <h4 className="text-white font-medium mb-2">University + Colleges Grassroots Visibility</h4>
                <p className="text-white/50 text-sm">Hands-on sessions at innovation labs</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 p-6 border border-blue-500/20">
                <Building2 className="w-10 h-10 text-blue-400 mb-4" />
                <h4 className="text-white font-medium mb-2">Accelerator House Activations</h4>
                <p className="text-white/50 text-sm">Embedded presence in founder programs</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 p-6 border border-blue-500/20">
                <Network className="w-10 h-10 text-blue-400 mb-4" />
                <h4 className="text-white font-medium mb-2">Founder Ecosystem Pop-ups</h4>
                <p className="text-white/50 text-sm">Strategic presence at key gatherings</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/5 p-6 border border-cyan-500/20">
                <Users className="w-10 h-10 text-cyan-400 mb-4" />
                <h4 className="text-white font-medium mb-2">Strategic Community Partnerships</h4>
                <p className="text-white/50 text-sm">Long-term ecosystem relationships</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-white/10">
              <p className="text-white/50 text-sm mb-4 uppercase tracking-wider">These generate:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="text-white/80 text-sm">Real usage</p>
                </div>
                <div className="text-center">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="text-white/80 text-sm">Organic content</p>
                </div>
                <div className="text-center">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="text-white/80 text-sm">Peer-driven adoption</p>
                </div>
                <div className="text-center">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="text-white/80 text-sm">Brand credibility</p>
                </div>
              </div>
            </div>
          </section>

          {/* 90-Day Focus */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <Calendar className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">90-Day Focus</h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative bg-gradient-to-br from-cyan-500/10 to-transparent rounded-2xl p-6 border border-cyan-500/20 overflow-hidden">
                <div className="absolute top-4 right-4 text-5xl font-bold text-cyan-500/10">1</div>
                <Clock className="w-8 h-8 text-cyan-400 mb-4" />
                <p className="text-cyan-400 text-sm font-medium mb-2">Month 1</p>
                <p className="text-white/80 text-sm leading-relaxed">Brand perception audit and builder lifecycle mapping</p>
              </div>
              <div className="relative bg-gradient-to-br from-blue-500/10 to-transparent rounded-2xl p-6 border border-blue-500/20 overflow-hidden">
                <div className="absolute top-4 right-4 text-5xl font-bold text-blue-500/10">2</div>
                <Zap className="w-8 h-8 text-blue-400 mb-4" />
                <p className="text-blue-400 text-sm font-medium mb-2">Month 2</p>
                <p className="text-white/80 text-sm leading-relaxed">Launch first high-signal activation</p>
              </div>
              <div className="relative bg-gradient-to-br from-cyan-500/10 to-transparent rounded-2xl p-6 border border-cyan-500/20 overflow-hidden">
                <div className="absolute top-4 right-4 text-5xl font-bold text-cyan-500/10">3</div>
                <TrendingUp className="w-8 h-8 text-cyan-400 mb-4" />
                <p className="text-cyan-400 text-sm font-medium mb-2">Month 3</p>
                <p className="text-white/80 text-sm leading-relaxed">University GTM strategy + power builder nurture system</p>
              </div>
            </div>
          </section>

          {/* Why I Am Aligned */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <User className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Why I Am Aligned</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">Founder and exited operator</span>
              </div>
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">Community Lead, TFA Reinvention Lab</span>
              </div>
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">Accelerator exp: Launch House, ACT House</span>
              </div>
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">GSV and AfroTech recognition</span>
              </div>
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">Heavy Emergent user deploying live systems</span>
              </div>
              <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white/80">Advised startup to migrate onto Emergent</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl p-6 border border-cyan-500/20 text-center">
              <p className="text-white/70 italic">
                I understand founder psychology, especially non-technical builders navigating overwhelm.
              </p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default EmergentPage;
